import React, { useState } from 'react';

// eslint-disable-next-line no-unused-vars
const fmt = (n) => Number(n).toLocaleString('en-IN');

const inputStyle = {
  width: '100%', padding: '11px 14px', borderRadius: '8px',
  border: '1px solid var(--border)', backgroundColor: 'var(--bg-secondary)', color: 'var(--text-primary)',
  fontSize: '14px', outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit',
};
const labelStyle = {
  fontSize: '12px', fontWeight: 600, color: 'var(--text-secondary)',
  textTransform: 'uppercase', letterSpacing: '0.07em', display: 'block', marginBottom: '6px',
};
const darkCard = { backgroundColor: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: '14px' };

const DECISION_TYPES = [
  { id: 'home',       label: 'Buy a Home',        icon: '🏠', hint: 'Rent vs buy, EMI affordability, right time to buy' },
  { id: 'career',     label: 'Career Move',        icon: '💼', hint: 'Switch companies, go freelance, accept promotion' },
  { id: 'investment', label: 'Investment',          icon: '📈', hint: 'Stocks, MF, FD, real estate allocation' },
  { id: 'business',   label: 'Start a Business',   icon: '🚀', hint: 'Side hustle, full-time venture, franchise' },
  { id: 'education',  label: 'Higher Education',   icon: '🎓', hint: 'MBA, online course, certification ROI' },
  { id: 'other',      label: 'Other Decision',     icon: '⚖️', hint: 'Any major life or financial decision' },
];

export default function DecisionIntelligence({ goBack }) {
  const [step, setStep] = useState('type');   // 'type' | 'form' | 'loading' | 'report'
  const [selectedType, setSelectedType] = useState(null);
  const [error, setError] = useState('');
  const [report, setReport] = useState(null);

  const [form, setForm] = useState({
    decisionTitle: '',
    monthlyIncome: '',
    monthlySavings: '',
    totalSavings: '',
    optionA: '',
    optionAcost: '',
    optionB: '',
    optionBcost: '',
    timeHorizonYears: '5',
    additionalContext: '',
  });

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.decisionTitle || !form.optionA) {
      setError('Please describe your decision and at least Option A.');
      return;
    }
    setStep('loading');
    try {
      const res = await fetch('/api/finance?action=decision-intelligence', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          decisionType:     selectedType,
          decisionTitle:    form.decisionTitle,
          monthlyIncome:    form.monthlyIncome ? Number(form.monthlyIncome.replace(/,/g, '')) : null,
          monthlySavings:   form.monthlySavings ? Number(form.monthlySavings.replace(/,/g, '')) : null,
          totalSavings:     form.totalSavings ? Number(form.totalSavings.replace(/,/g, '')) : null,
          optionA:          form.optionA,
          optionAcost:      form.optionAcost ? Number(form.optionAcost.replace(/,/g, '')) : null,
          optionB:          form.optionB || null,
          optionBcost:      form.optionBcost ? Number(form.optionBcost.replace(/,/g, '')) : null,
          timeHorizonYears: Number(form.timeHorizonYears),
          additionalContext: form.additionalContext,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Analysis failed');
      setReport(data);
      setStep('report');
    } catch (err) {
      setError(err.message);
      setStep('form');
    }
  };

  // ── STEP 1: Decision type picker ─────────────────────────────────────────
  if (step === 'type') return (
    <section style={{ minHeight: '100vh', backgroundColor: 'var(--bg)', paddingTop: '80px' }}>
      <div style={{ maxWidth: '680px', margin: '0 auto', padding: '40px 24px' }}>
        <button onClick={goBack} style={{ background: 'none', border: 'none', color: 'var(--text-secondary)', fontSize: '13px', cursor: 'pointer', padding: 0, marginBottom: '32px', fontFamily: 'inherit' }}>← Back</button>
        <div style={{ marginBottom: '32px' }}>
          <div style={{ display: 'inline-block', backgroundColor: 'rgba(0,194,124,0.08)', border: '1px solid rgba(34,211,238,0.3)', borderRadius: '6px', padding: '4px 10px', fontSize: '11px', fontWeight: 700, color: 'var(--accent)', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: '14px' }}>Decision Intelligence</div>
          <h1 style={{ fontSize: '28px', fontWeight: 700, color: 'var(--text-primary)', margin: '0 0 10px', letterSpacing: '-0.02em' }}>What are you deciding?</h1>
          <p style={{ fontSize: '14px', color: 'var(--text-secondary)', margin: 0, lineHeight: 1.7 }}>Model any major financial or life decision with real numbers and AI-backed analysis.</p>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
          {DECISION_TYPES.map(dt => (
            <button
              key={dt.id}
              onClick={() => { setSelectedType(dt.id); setStep('form'); }}
              style={{ ...darkCard, padding: '20px', textAlign: 'left', cursor: 'pointer', background: 'var(--bg-secondary)', transition: 'all 0.2s', border: '1px solid var(--border)' }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--accent)60'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.transform = 'translateY(0)'; }}
            >
              <span style={{ fontSize: '24px', display: 'block', marginBottom: '10px' }}>{dt.icon}</span>
              <p style={{ fontSize: '14px', fontWeight: 600, color: 'var(--text-primary)', margin: '0 0 4px' }}>{dt.label}</p>
              <p style={{ fontSize: '12px', color: 'var(--text-secondary)', margin: 0, lineHeight: 1.5 }}>{dt.hint}</p>
            </button>
          ))}
        </div>
      </div>
    </section>
  );

  // ── STEP 2: Form ─────────────────────────────────────────────────────────
  if (step === 'form') {
    const dt = DECISION_TYPES.find(d => d.id === selectedType);
    return (
      <section style={{ minHeight: '100vh', backgroundColor: 'var(--bg)', paddingTop: '80px' }}>
        <div style={{ maxWidth: '640px', margin: '0 auto', padding: '40px 24px' }}>
          <button onClick={() => setStep('type')} style={{ background: 'none', border: 'none', color: 'var(--text-secondary)', fontSize: '13px', cursor: 'pointer', padding: 0, marginBottom: '32px', fontFamily: 'inherit' }}>← Change decision type</button>
          <div style={{ marginBottom: '28px' }}>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', backgroundColor: 'rgba(0,194,124,0.08)', border: '1px solid rgba(34,211,238,0.3)', borderRadius: '6px', padding: '4px 12px', fontSize: '12px', fontWeight: 700, color: 'var(--accent)', marginBottom: '14px' }}>
              <span>{dt?.icon}</span><span>{dt?.label}</span>
            </div>
            <h1 style={{ fontSize: '26px', fontWeight: 700, color: 'var(--text-primary)', margin: '0 0 8px', letterSpacing: '-0.02em' }}>Decision Details</h1>
            <p style={{ fontSize: '14px', color: 'var(--text-secondary)', margin: 0 }}>The more numbers you give, the sharper the analysis.</p>
          </div>

          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            {/* Decision */}
            <div style={{ ...darkCard, padding: '24px' }}>
              <p style={{ fontSize: '12px', fontWeight: 700, color: 'var(--accent)', textTransform: 'uppercase', letterSpacing: '0.08em', margin: '0 0 16px' }}>The Decision</p>
              <div style={{ marginBottom: '14px' }}>
                <label style={labelStyle}>Decision Title</label>
                <input style={inputStyle} placeholder={`e.g. Should I ${dt?.id === 'home' ? 'buy a 2BHK in Bangalore for Rs.80L' : dt?.id === 'career' ? 'join a startup for 40% hike' : 'invest Rs.10L in index funds'}`} value={form.decisionTitle} onChange={set('decisionTitle')} />
              </div>
              <div style={{ marginBottom: '14px' }}>
                <label style={labelStyle}>Option A — What you're considering</label>
                <textarea style={{ ...inputStyle, height: '70px', resize: 'vertical' }} placeholder="Describe Option A in detail — what does it involve?" value={form.optionA} onChange={set('optionA')} />
              </div>
              <div style={{ marginBottom: '14px' }}>
                <label style={labelStyle}>Option A — Cost / Commitment (Rs.)</label>
                <input style={inputStyle} placeholder="Total cost or upfront amount" value={form.optionAcost} onChange={set('optionAcost')} />
              </div>
              <div style={{ marginBottom: '14px' }}>
                <label style={labelStyle}>Option B — The alternative (optional)</label>
                <textarea style={{ ...inputStyle, height: '70px', resize: 'vertical' }} placeholder="e.g. Continuing to rent, staying at current job, keeping cash in FD" value={form.optionB} onChange={set('optionB')} />
              </div>
              <div>
                <label style={labelStyle}>Option B — Cost / Commitment (Rs.)</label>
                <input style={inputStyle} placeholder="Optional" value={form.optionBcost} onChange={set('optionBcost')} />
              </div>
            </div>

            {/* Financial context */}
            <div style={{ ...darkCard, padding: '24px' }}>
              <p style={{ fontSize: '12px', fontWeight: 700, color: 'var(--accent)', textTransform: 'uppercase', letterSpacing: '0.08em', margin: '0 0 16px' }}>Your Financial Context</p>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
                <div>
                  <label style={labelStyle}>Monthly Income (Rs.)</label>
                  <input style={inputStyle} placeholder="Take-home" value={form.monthlyIncome} onChange={set('monthlyIncome')} />
                </div>
                <div>
                  <label style={labelStyle}>Monthly Savings (Rs.)</label>
                  <input style={inputStyle} placeholder="After expenses" value={form.monthlySavings} onChange={set('monthlySavings')} />
                </div>
                <div>
                  <label style={labelStyle}>Total Savings / Net Worth (Rs.)</label>
                  <input style={inputStyle} placeholder="Liquid + investments" value={form.totalSavings} onChange={set('totalSavings')} />
                </div>
                <div>
                  <label style={labelStyle}>Time Horizon (Years)</label>
                  <input style={inputStyle} type="number" min="1" max="30" value={form.timeHorizonYears} onChange={set('timeHorizonYears')} />
                </div>
              </div>
            </div>

            {/* Extra context */}
            <div>
              <label style={labelStyle}>Anything else to consider?</label>
              <textarea style={{ ...inputStyle, height: '80px', resize: 'vertical' }} placeholder="e.g. I have a 3-year-old, job security is uncertain, interest rates are high" value={form.additionalContext} onChange={set('additionalContext')} />
            </div>

            {error && <p style={{ fontSize: '13px', color: '#EF4444', margin: 0 }}>{error}</p>}

            <button type="submit" style={{ width: '100%', padding: '13px', borderRadius: '9px', background: 'var(--accent)', color: 'var(--bg)', border: 'none', fontSize: '14px', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
              Analyse Decision →
            </button>
          </form>
        </div>
      </section>
    );
  }

  // ── LOADING ───────────────────────────────────────────────────────────────
  if (step === 'loading') return (
    <section style={{ minHeight: '100vh', backgroundColor: 'var(--bg)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ textAlign: 'center' }}>
        <div style={{ width: '48px', height: '48px', border: '3px solid var(--border)', borderTopColor: 'var(--accent)', borderRadius: '50%', animation: 'spin 0.9s linear infinite', margin: '0 auto 20px' }} />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        <p style={{ color: 'var(--text-secondary)', fontSize: '14px' }}>Modelling your decision…</p>
      </div>
    </section>
  );

  // ── REPORT ────────────────────────────────────────────────────────────────
  // eslint-disable-next-line no-unused-vars
  const { recommendation, financialProjections, keyRisks, actionSteps, verdict, decisionScore } = report;
  const dt = DECISION_TYPES.find(d => d.id === selectedType);

  return (
    <section style={{ minHeight: '100vh', backgroundColor: 'var(--bg)', paddingTop: '80px' }}>
      <div style={{ maxWidth: '800px', margin: '0 auto', padding: '40px 24px' }}>

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '28px', flexWrap: 'wrap', gap: '12px' }}>
          <div>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', backgroundColor: 'rgba(0,194,124,0.08)', border: '1px solid rgba(34,211,238,0.3)', borderRadius: '6px', padding: '4px 12px', fontSize: '11px', fontWeight: 700, color: 'var(--accent)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '10px' }}>
              {dt?.icon} Decision Analysis
            </div>
            <h1 style={{ fontSize: '22px', fontWeight: 700, color: 'var(--text-primary)', margin: 0, letterSpacing: '-0.02em' }}>{form.decisionTitle}</h1>
          </div>
          <button onClick={() => setStep('form')} style={{ background: 'transparent', border: '1px solid var(--border)', color: 'var(--text-secondary)', borderRadius: '8px', padding: '8px 16px', fontSize: '13px', cursor: 'pointer', fontFamily: 'inherit', flexShrink: 0 }}>Recalculate</button>
        </div>

        {/* Recommendation banner */}
        {recommendation && (
          <div style={{ ...darkCard, padding: '20px 24px', marginBottom: '16px', borderLeft: '3px solid var(--accent)' }}>
            <p style={{ fontSize: '11px', fontWeight: 700, color: 'var(--accent)', textTransform: 'uppercase', letterSpacing: '0.08em', margin: '0 0 8px' }}>Recommendation</p>
            <p style={{ fontSize: '14px', color: 'var(--text-primary)', lineHeight: 1.75, margin: 0 }}>{recommendation}</p>
          </div>
        )}

        {/* Decision score */}
        {decisionScore && (
          <div style={{ display: 'grid', gridTemplateColumns: form.optionB ? '1fr 1fr' : '1fr', gap: '12px', marginBottom: '20px' }}>
            {['optionA', 'optionB'].filter(k => k === 'optionA' || form.optionB).map(k => {
              const score = decisionScore[k];
              if (!score) return null;
              const isA = k === 'optionA';
              const color = score.verdict === 'recommended' ? '#10B981' : score.verdict === 'caution' ? '#F59E0B' : 'var(--text-secondary)';
              return (
                <div key={k} style={{ ...darkCard, padding: '18px 20px', borderTop: `3px solid ${color}` }}>
                  <p style={{ fontSize: '11px', fontWeight: 700, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.08em', margin: '0 0 6px' }}>{isA ? 'Option A' : 'Option B'}</p>
                  <p style={{ fontSize: '13px', color: 'var(--text-primary)', margin: '0 0 10px', lineHeight: 1.5 }}>{isA ? form.optionA.slice(0, 80) : form.optionB?.slice(0, 80)}{(isA ? form.optionA : form.optionB)?.length > 80 ? '…' : ''}</p>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <span style={{ fontSize: '22px', fontWeight: 700, color }}>{score.score}/10</span>
                    <span style={{ fontSize: '11px', fontWeight: 700, color, textTransform: 'uppercase', letterSpacing: '0.07em', border: `1px solid ${color}40`, borderRadius: '4px', padding: '2px 8px' }}>{score.verdict}</span>
                  </div>
                  {score.summary && <p style={{ fontSize: '12px', color: 'var(--text-secondary)', margin: '8px 0 0', lineHeight: 1.6 }}>{score.summary}</p>}
                </div>
              );
            })}
          </div>
        )}

        {/* Financial Projections */}
        {financialProjections?.length > 0 && (
          <div style={{ marginBottom: '24px' }}>
            <p style={{ fontSize: '11px', fontWeight: 700, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.1em', margin: '0 0 12px' }}>Financial Projections</p>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '10px' }}>
              {financialProjections.map((p, i) => (
                <div key={i} style={{ ...darkCard, padding: '16px 18px' }}>
                  <p style={{ fontSize: '11px', color: 'var(--text-tertiary)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.07em', margin: '0 0 6px' }}>{p.label}</p>
                  <p style={{ fontSize: '18px', fontWeight: 700, color: p.positive === false ? '#EF4444' : '#10B981', margin: '0 0 4px' }}>{p.value}</p>
                  {p.note && <p style={{ fontSize: '12px', color: 'var(--text-secondary)', margin: 0, lineHeight: 1.5 }}>{p.note}</p>}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Key Risks */}
        {keyRisks?.length > 0 && (
          <div style={{ marginBottom: '24px' }}>
            <p style={{ fontSize: '11px', fontWeight: 700, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.1em', margin: '0 0 12px' }}>Key Risks</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {keyRisks.map((r, i) => (
                <div key={i} style={{ ...darkCard, padding: '14px 18px', display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                  <span style={{ fontSize: '14px', flexShrink: 0, marginTop: '1px' }}>⚠️</span>
                  <div>
                    <p style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-primary)', margin: '0 0 3px' }}>{r.risk}</p>
                    <p style={{ fontSize: '12px', color: 'var(--text-secondary)', margin: '0 0 5px', lineHeight: 1.6 }}>{r.detail}</p>
                    {r.mitigation && <p style={{ fontSize: '12px', color: '#10B981', margin: 0, fontWeight: 500 }}>Mitigation: {r.mitigation}</p>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Action Steps */}
        {actionSteps?.length > 0 && (
          <div style={{ marginBottom: '40px' }}>
            <p style={{ fontSize: '11px', fontWeight: 700, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.1em', margin: '0 0 12px' }}>Action Steps</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {actionSteps.map((a, i) => (
                <div key={i} style={{ ...darkCard, padding: '14px 18px', display: 'flex', gap: '14px', alignItems: 'flex-start' }}>
                  <span style={{ fontSize: '13px', fontWeight: 700, color: 'var(--accent)', flexShrink: 0, minWidth: '20px' }}>{i + 1}</span>
                  <div>
                    <p style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-primary)', margin: '0 0 2px' }}>{a.step}</p>
                    {a.detail && <p style={{ fontSize: '12px', color: 'var(--text-secondary)', margin: 0, lineHeight: 1.6 }}>{a.detail}</p>}
                  </div>
                  {a.timeframe && <span style={{ fontSize: '11px', color: 'var(--text-tertiary)', flexShrink: 0, marginLeft: 'auto' }}>{a.timeframe}</span>}
                </div>
              ))}
            </div>
          </div>
        )}

        <button onClick={goBack} style={{ background: 'transparent', border: '1px solid var(--border)', color: 'var(--text-secondary)', borderRadius: '8px', padding: '10px 20px', fontSize: '13px', cursor: 'pointer', fontFamily: 'inherit' }}>← Back to Dashboard</button>
      </div>
    </section>
  );
}
